/** "What moves the number" panel: runs the sensitivity levers on demand and
 *  shows how far each one shifts the total. */
import React from 'react';
import { CRC } from './moduleInputsData';
import { money } from './spreadSplitData';
import { Sensitivity, Variant } from './spreadSensitivity';

export type SensitivityState =
  | { status: 'idle' }
  | { status: 'running'; done: number; total: number }
  | { status: 'done'; result: Sensitivity }
  | { status: 'error'; message: string };

interface Props {
  state: SensitivityState;
  onRun: () => void;
  /** Blocks the run while the base total is still settling. */
  disabledReason?: string;
}

const card: React.CSSProperties = {
  background: '#fff', border: `1px solid ${CRC.border}`, borderRadius: 12,
  boxShadow: '0 1px 3px rgba(0,0,0,0.04)', padding: 20,
};
const cell: React.CSSProperties = { fontSize: 13, color: CRC.textGrey, padding: '7px 0' };
const runBtn = (disabled: boolean): React.CSSProperties => ({
  padding: '9px 16px', borderRadius: 8, border: 'none',
  background: disabled ? CRC.pageBg : CRC.readableRed, color: disabled ? CRC.heritageGrey : '#fff',
  fontWeight: 600, fontSize: 13, fontFamily: 'inherit', cursor: disabled ? 'default' : 'pointer',
});

const signed = (n: number) => `${n >= 0 ? '+' : '−'}${money(Math.abs(n))}`;
const signedPct = (n: number) => `${n >= 0 ? '+' : '−'}${(Math.abs(n) * 100).toFixed(1)}%`;
const tone = (delta: number) => (delta === 0 ? CRC.heritageGrey : delta > 0 ? CRC.blueprintBlue : CRC.readableRed);

const VariantRow: React.FC<{ variant: Variant }> = ({ variant }) => (
  <tr style={{ borderTop: `1px solid ${CRC.border}` }}>
    <td style={{ ...cell, paddingLeft: 14, color: CRC.heritageGrey }}>{variant.label}</td>
    <td style={{ ...cell, textAlign: 'right' }}>{money(variant.total)}</td>
    <td style={{ ...cell, textAlign: 'right', fontWeight: 600, color: tone(variant.delta) }}>{signed(variant.delta)}</td>
    <td style={{ ...cell, textAlign: 'right', color: tone(variant.delta) }}>{signedPct(variant.deltaShare)}</td>
  </tr>
);

export const DefenceSensitivity: React.FC<Props> = ({ state, onRun, disabledReason }) => {
  const running = state.status === 'running';
  const disabled = running || !!disabledReason;

  return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontFamily: 'Funnel Sans', fontSize: 15, fontWeight: 600, color: CRC.blueprintBlue }}>What moves the number</div>
          <div style={{ fontSize: 11.5, color: CRC.heritageGrey, marginTop: 3 }}>
            Re-prices the whole project with one input changed at a time. Every figure is a real engine result, not an estimate.
          </div>
        </div>
        <button onClick={onRun} disabled={disabled} style={runBtn(disabled)}>
          {running ? `Pricing ${state.done}/${state.total}…` : state.status === 'done' ? 'Recalculate' : 'Run'}
        </button>
      </div>

      {disabledReason && !running && (
        <div style={{ fontSize: 11.5, color: CRC.heritageGrey, marginTop: 10 }}>{disabledReason}</div>
      )}

      {state.status === 'error' && (
        <div style={{ fontSize: 12, color: CRC.readableRed, marginTop: 12 }}>{state.message}</div>
      )}

      {state.status === 'done' && (
        <>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 14 }}>
            <thead>
              <tr>
                {['Lever', 'New total', 'Change', ''].map((h, i) => (
                  <th key={h || i} style={{ fontSize: 10.5, letterSpacing: '0.05em', textTransform: 'uppercase', color: CRC.heritageGrey, textAlign: i === 0 ? 'left' : 'right', paddingBottom: 6, fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {state.result.levers.map((lever) => (
                <React.Fragment key={lever.key}>
                  <tr style={{ borderTop: `1px solid ${CRC.border}` }}>
                    <td style={{ ...cell, fontWeight: 600, color: CRC.blueprintBlue }} colSpan={4}>{lever.label}</td>
                  </tr>
                  {lever.variants.map((v) => <VariantRow key={v.label} variant={v} />)}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          {state.result.biggestLever && (
            <div style={{ fontSize: 12.5, color: CRC.textGrey, marginTop: 14, paddingTop: 12, borderTop: `1px solid ${CRC.border}` }}>
              Biggest lever: <strong style={{ color: CRC.blueprintBlue }}>{state.result.biggestLever}</strong>. Expect the most movement there if the customer pushes on scope.
            </div>
          )}
        </>
      )}
    </div>
  );
};
